import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, Plus, Edit, Trash2, FileText, Receipt, Calendar,
  DollarSign, AlertTriangle, CheckCircle, XCircle, Clock,
  RefreshCw, CreditCard, ShieldAlert, Building2, Users,
  TrendingUp, Send, Upload
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const COMPLIANCE_TYPES = [
  { id: "fire_noc", name: "Fire NOC" },
  { id: "insurance", name: "Insurance Policy" },
  { id: "trade_license", name: "Trade License" },
  { id: "security_clearance", name: "Security Clearance" },
  { id: "health_permit", name: "Health Permit" },
  { id: "fssai", name: "FSSAI License" }
];

export default function MallPostLeasingPage() {
  const { mallId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mall, setMall] = useState(null);
  const [units, setUnits] = useState([]);
  const [leases, setLeases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [riskAlerts, setRiskAlerts] = useState({ alerts: [], total_alerts: 0 });
  const [activeTab, setActiveTab] = useState("leases");
  
  // Dialogs
  const [showLeaseDialog, setShowLeaseDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showComplianceDialog, setShowComplianceDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedLease, setSelectedLease] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Forms
  const [leaseForm, setLeaseForm] = useState({
    tenant_id: "",
    unit_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    lockin_period_months: 36,
    base_rent_per_sqft: 200,
    escalation_percent: 5,
    escalation_frequency_months: 12,
    revenue_share_percent: 0,
    minimum_guarantee: 0,
    cam_type: "fixed",
    cam_per_sqft: 50,
    rent_free_months: 1,
    deposit_amount: 0
  });

  const [complianceForm, setComplianceForm] = useState({
    tenant_id: "",
    document_type: "fire_noc",
    expiry_date: "",
    document_url: ""
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_date: new Date().toISOString().slice(0, 10),
    payment_reference: ""
  });

  const [generateMonth, setGenerateMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    if (mallId && token) {
      fetchData();
    }
  }, [mallId, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch mall
      const mallRes = await axios.get(`${API}/api/malls/${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMall(mallRes.data);

      // Fetch units
      const unitsRes = await axios.get(`${API}/api/shops?mall_id=${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnits(unitsRes.data);

      // Fetch leases for this mall
      const leasesRes = await axios.get(`${API}/api/post-leasing/leases?mall_id=${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeases(leasesRes.data);

      // Fetch invoices
      const invoicesRes = await axios.get(`${API}/api/post-leasing/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(invoicesRes.data);

      // Fetch compliance
      const complianceRes = await axios.get(`${API}/api/post-leasing/compliance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompliance(complianceRes.data);

      // Fetch risk alerts
      const alertsRes = await axios.get(`${API}/api/post-leasing/risk-alerts?mall_id=${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRiskAlerts(alertsRes.data);

    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLease = async () => {
    try {
      // Calculate deposit based on rent
      const unit = units.find(u => u.id === leaseForm.unit_id);
      if (!unit) {
        toast.error("Please select a unit");
        return;
      }

      const monthlyRent = unit.carpet_area_sqft * leaseForm.base_rent_per_sqft;
      const depositAmount = monthlyRent * 6; // 6 months security deposit

      const leaseData = {
        ...leaseForm,
        deposit_amount: depositAmount
      };

      await axios.post(`${API}/api/post-leasing/leases`, leaseData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Lease created successfully");
      setShowLeaseDialog(false);
      resetLeaseForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create lease");
    }
  };

  const handleRenewLease = async (leaseId) => {
    const newEndDate = prompt("Enter new end date (YYYY-MM-DD):", 
      new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
    
    if (!newEndDate) return;

    try {
      await axios.put(
        `${API}/api/post-leasing/leases/${leaseId}/renew?new_end_date=${newEndDate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Lease renewed");
      fetchData();
    } catch (err) {
      toast.error("Failed to renew lease");
    }
  };

  const handleTerminateLease = async (leaseId) => {
    if (!confirm("Are you sure you want to terminate this lease?")) return;

    const terminationDate = prompt("Enter termination date (YYYY-MM-DD):", 
      new Date().toISOString().slice(0, 10));
    
    if (!terminationDate) return;

    try {
      await axios.put(
        `${API}/api/post-leasing/leases/${leaseId}/terminate?termination_date=${terminationDate}&reason=Early termination`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Lease terminated");
      fetchData();
    } catch (err) {
      toast.error("Failed to terminate lease");
    }
  };

  const handleGenerateInvoices = async () => {
    try {
      const res = await axios.post(
        `${API}/api/post-leasing/invoices/generate-monthly?month=${generateMonth}&mall_id=${mallId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Generated ${res.data.generated} invoices for ${generateMonth}`);
      setShowInvoiceDialog(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to generate invoices");
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice) return;

    try {
      await axios.put(
        `${API}/api/post-leasing/invoices/${selectedInvoice}/record-payment?amount=${paymentForm.amount}&payment_date=${paymentForm.payment_date}&payment_reference=${paymentForm.payment_reference}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment recorded");
      setShowPaymentDialog(false);
      setSelectedInvoice(null);
      resetPaymentForm();
      fetchData();
    } catch (err) {
      toast.error("Failed to record payment");
    }
  };

  const handleCreateCompliance = async () => {
    try {
      await axios.post(`${API}/api/post-leasing/compliance`, complianceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Compliance record created");
      setShowComplianceDialog(false);
      resetComplianceForm();
      fetchData();
    } catch (err) {
      toast.error("Failed to create compliance record");
    }
  };

  const resetLeaseForm = () => {
    setLeaseForm({
      tenant_id: "",
      unit_id: "",
      start_date: new Date().toISOString().slice(0, 10),
      end_date: new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      lockin_period_months: 36,
      base_rent_per_sqft: 200,
      escalation_percent: 5,
      escalation_frequency_months: 12,
      revenue_share_percent: 0,
      minimum_guarantee: 0,
      cam_type: "fixed",
      cam_per_sqft: 50,
      rent_free_months: 1,
      deposit_amount: 0
    });
  };

  const resetComplianceForm = () => {
    setComplianceForm({
      tenant_id: "",
      document_type: "fire_noc",
      expiry_date: "",
      document_url: ""
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: 0,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_reference: ""
    });
  };

  const formatCurrency = (val) => {
    if (!val) return "₹0";
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    return `₹${val.toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "default",
      expired: "destructive",
      terminated: "destructive",
      renewed: "secondary",
      paid: "default",
      overdue: "destructive",
      partially_paid: "outline",
      generated: "secondary",
      valid: "default",
      expiring_soon: "outline"
    };
    return <Badge variant={variants[status] || "secondary"} className="capitalize">{status?.replace(/_/g, ' ')}</Badge>;
  };

  const occupiedUnits = units.filter(u => u.status === "occupied");
  const totalMonthlyRent = leases.reduce((sum, l) => sum + (l.monthly_rent || 0), 0);
  const totalOutstanding = invoices.reduce((sum, i) => sum + ((i.total_due || 0) - (i.paid_amount || 0)), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mall-post-leasing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/mall/${mallId}/manage`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Post-Leasing: {mall?.name}</h1>
            <p className="text-muted-foreground">Lease management, billing & compliance</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/mall/${mallId}/pre-leasing`)}>
            Pre-Leasing
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Leases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leases.filter(l => l.status === "active").length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{formatCurrency(totalMonthlyRent)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{formatCurrency(totalOutstanding)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expiring (90 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">
              {leases.filter(l => l.days_to_expiry > 0 && l.days_to_expiry <= 90).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Risk Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{riskAlerts.total_alerts}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="leases">Leases</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="alerts">
            Alerts {riskAlerts.total_alerts > 0 && <Badge variant="destructive" className="ml-1">{riskAlerts.total_alerts}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Leases Tab */}
        <TabsContent value="leases" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Lease Management ({leases.length})</CardTitle>
              <Dialog open={showLeaseDialog} onOpenChange={setShowLeaseDialog}>
                <DialogTrigger asChild>
                  <Button onClick={resetLeaseForm}>
                    <Plus className="h-4 w-4 mr-2" /> New Lease
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create New Lease</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
                    <div className="space-y-2">
                      <Label>Select Unit</Label>
                      <Select value={leaseForm.unit_id} onValueChange={(v) => setLeaseForm({...leaseForm, unit_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Select a unit" /></SelectTrigger>
                        <SelectContent>
                          {units.filter(u => u.status === "vacant").map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.unit_number} - Floor {u.floor} ({u.carpet_area_sqft} sq ft)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tenant ID (Brand Partner User ID)</Label>
                      <Input 
                        value={leaseForm.tenant_id}
                        onChange={(e) => setLeaseForm({...leaseForm, tenant_id: e.target.value})}
                        placeholder="Enter tenant user ID"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date</Label>
                        <Input 
                          type="date"
                          value={leaseForm.start_date}
                          onChange={(e) => setLeaseForm({...leaseForm, start_date: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input 
                          type="date"
                          value={leaseForm.end_date}
                          onChange={(e) => setLeaseForm({...leaseForm, end_date: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Base Rent (₹/sq ft)</Label>
                        <Input 
                          type="number"
                          value={leaseForm.base_rent_per_sqft}
                          onChange={(e) => setLeaseForm({...leaseForm, base_rent_per_sqft: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CAM (₹/sq ft)</Label>
                        <Input 
                          type="number"
                          value={leaseForm.cam_per_sqft}
                          onChange={(e) => setLeaseForm({...leaseForm, cam_per_sqft: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Escalation %</Label>
                        <Input 
                          type="number"
                          value={leaseForm.escalation_percent}
                          onChange={(e) => setLeaseForm({...leaseForm, escalation_percent: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Lock-in (months)</Label>
                        <Input 
                          type="number"
                          value={leaseForm.lockin_period_months}
                          onChange={(e) => setLeaseForm({...leaseForm, lockin_period_months: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Revenue Share %</Label>
                        <Input 
                          type="number"
                          value={leaseForm.revenue_share_percent}
                          onChange={(e) => setLeaseForm({...leaseForm, revenue_share_percent: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rent-free (months)</Label>
                        <Input 
                          type="number"
                          value={leaseForm.rent_free_months}
                          onChange={(e) => setLeaseForm({...leaseForm, rent_free_months: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowLeaseDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateLease}>Create Lease</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leases.map(lease => (
                  <div key={lease.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium">{lease.tenant_name || "Tenant"}</div>
                      <div className="text-sm text-muted-foreground">
                        {lease.unit_number} • {lease.start_date} to {lease.end_date}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(lease.monthly_rent)}/mo</div>
                        {lease.days_to_expiry > 0 && lease.days_to_expiry <= 90 && (
                          <div className="text-sm text-amber-500">{lease.days_to_expiry} days left</div>
                        )}
                      </div>
                      {getStatusBadge(lease.status)}
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => handleRenewLease(lease.id)}>
                          Renew
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleTerminateLease(lease.id)}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {leases.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No leases found. Create a lease to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Invoices & Billing ({invoices.length})</CardTitle>
              <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Receipt className="h-4 w-4 mr-2" /> Generate Monthly Invoices
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate Monthly Invoices</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Input 
                        type="month"
                        value={generateMonth}
                        onChange={(e) => setGenerateMonth(e.target.value)}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                      This will generate invoices for all active leases in {mall?.name} for the selected month.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
                    <Button onClick={handleGenerateInvoices}>Generate Invoices</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {invoices.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium">{inv.tenant_name || "Tenant"}</div>
                      <div className="text-sm text-muted-foreground">
                        {inv.unit_number} • {inv.month} • Due: {inv.due_date}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(inv.total_due)}</div>
                        {inv.balance > 0 && (
                          <div className="text-sm text-red-500">Balance: {formatCurrency(inv.balance)}</div>
                        )}
                      </div>
                      {getStatusBadge(inv.status)}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedInvoice(inv.id);
                          setPaymentForm({
                            ...paymentForm,
                            amount: inv.balance || inv.total_due
                          });
                          setShowPaymentDialog(true);
                        }}
                        disabled={inv.status === "paid"}
                      >
                        <CreditCard className="h-4 w-4 mr-1" /> Pay
                      </Button>
                    </div>
                  </div>
                ))}
                {invoices.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No invoices found. Generate monthly invoices to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Compliance Records ({compliance.length})</CardTitle>
              <Dialog open={showComplianceDialog} onOpenChange={setShowComplianceDialog}>
                <DialogTrigger asChild>
                  <Button onClick={resetComplianceForm}>
                    <Plus className="h-4 w-4 mr-2" /> Add Compliance Record
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Compliance Record</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label>Tenant ID</Label>
                      <Input 
                        value={complianceForm.tenant_id}
                        onChange={(e) => setComplianceForm({...complianceForm, tenant_id: e.target.value})}
                        placeholder="Enter tenant ID"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select 
                        value={complianceForm.document_type} 
                        onValueChange={(v) => setComplianceForm({...complianceForm, document_type: v})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {COMPLIANCE_TYPES.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Expiry Date</Label>
                      <Input 
                        type="date"
                        value={complianceForm.expiry_date}
                        onChange={(e) => setComplianceForm({...complianceForm, expiry_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Document URL (optional)</Label>
                      <Input 
                        value={complianceForm.document_url}
                        onChange={(e) => setComplianceForm({...complianceForm, document_url: e.target.value})}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowComplianceDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateCompliance}>Add Record</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {compliance.map(rec => (
                  <div key={rec.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium capitalize">{rec.document_type?.replace(/_/g, ' ')}</div>
                      <div className="text-sm text-muted-foreground">
                        {rec.tenant_name} • Expires: {rec.expiry_date}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {rec.days_to_expiry <= 30 && rec.days_to_expiry > 0 && (
                        <span className="text-amber-500 text-sm">{rec.days_to_expiry} days left</span>
                      )}
                      {rec.days_to_expiry < 0 && (
                        <span className="text-red-500 text-sm">Expired</span>
                      )}
                      {getStatusBadge(rec.status)}
                    </div>
                  </div>
                ))}
                {compliance.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No compliance records found.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Risk Alerts ({riskAlerts.total_alerts})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {riskAlerts.alerts?.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-muted-foreground">No risk alerts. Everything looks good!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {riskAlerts.alerts?.map((alert, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        alert.severity === 'critical' ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {alert.severity === 'critical' ? (
                          <XCircle className="h-5 w-5 text-red-500" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-amber-500" />
                        )}
                        <div>
                          <div className="font-medium capitalize">{alert.type?.replace(/_/g, ' ')}</div>
                          <div className="text-sm text-muted-foreground">{alert.message}</div>
                        </div>
                      </div>
                      <Badge variant={alert.severity === 'critical' ? 'destructive' : 'outline'}>
                        {alert.severity}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input 
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input 
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Reference / Transaction ID</Label>
              <Input 
                value={paymentForm.payment_reference}
                onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                placeholder="e.g., TXN123456"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
